/**
 * The title screen (task 9.4).
 *
 * Continue appears only when a run is actually in progress, and names the
 * mission, the day and the difficulty it resumes — a resume button that does not
 * say what it resumes is a button nobody presses (`game-shell` spec, "Title
 * screen"). With no save at all, the primary action starts the first mission.
 */
import type { JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary } from '../components/AsyncBoundary.js';
import { Button, ButtonLink } from '../components/Button.js';
import { Card, CardBody } from '../components/Card.js';
import { missionsInOrder, type Catalog } from '../lib/catalog.js';
import { lockStateOf } from '../lib/catalog.js';
import { useContentSource } from '../lib/content-context.js';
import { completedMissionIds } from '../lib/save/types.js';
import { useAsync } from '../hooks/use-async.js';
import { useActiveProfile, useViewStore } from '../store/view-store.js';

/** The lowest-ordered mission the player can actually start. */
function firstPlayable(catalog: Catalog, completed: readonly string[]): string | null {
  const ordered = missionsInOrder(catalog);
  const next = ordered.find(
    (entry) => !completed.includes(entry.id) && !lockStateOf(catalog, entry, completed).locked,
  );
  return (
    (next ?? ordered.find((entry) => !lockStateOf(catalog, entry, completed).locked))?.id ?? null
  );
}

/**
 * Said once, and only when something is actually wrong (task 13.7).
 *
 * Two different problems reach here and they need different words. Storage
 * being denied means nothing this session will be kept; stored data being
 * unreadable — corrupt, or written by a newer build — means a fresh save has
 * been started *beside* it, and the important promise is that the old one has
 * not been destroyed.
 *
 * Dismissing sets `warned`, which is what makes it once rather than every time
 * the player passes through the title screen.
 */
function StorageWarning(): JSX.Element | null {
  const storage = useViewStore((state) => state.storage);
  const acknowledge = useViewStore((state) => state.acknowledgeStorageWarning);
  if (storage.warned || (!storage.ephemeral && !storage.recovered)) return null;

  return (
    <div
      role="alert"
      data-testid="storage-warning"
      data-kind={storage.recovered ? 'unreadable' : 'ephemeral'}
      className="mb-6 rounded-lg border border-warning bg-warning-soft p-4 text-sm text-ink"
    >
      <p className="font-medium">
        {storage.recovered
          ? 'Your saved data could not be read.'
          : 'This device will not keep your progress.'}
      </p>
      {storage.reason === null ? null : (
        <p className="mt-1 text-ink-muted" data-testid="storage-warning-reason">
          {storage.reason}
        </p>
      )}
      <p className="mt-1 text-ink-muted">
        {storage.recovered
          ? 'A fresh save has been started. Your earlier progress on this device is left untouched, not overwritten.'
          : 'This browser session cannot keep saved data. When you close the tab, you lose completed missions, banked Corporate Experience and any run in progress. Everything else in the game works normally.'}
      </p>
      <Button className="mt-3" onClick={acknowledge} data-testid="storage-warning-dismiss">
        Dismiss
      </Button>
    </div>
  );
}

export function TitleScreen(): JSX.Element {
  const source = useContentSource();
  const navigate = useNavigate();
  const profile = useActiveProfile();
  const catalog = useAsync(() => source.catalog(), [source]);
  const run = profile?.run ?? null;

  /*
   * Two elements, because the inset and the gutter are two different paddings.
   *
   * `px-safe` and `px-4` both set the horizontal padding, and `px-safe` is emitted
   * later in the stylesheet at equal specificity, so on one element `px-safe` wins.
   * On a phone held in portrait it resolves to `0px`, which put this screen flush
   * against both edges. Split across two elements the paddings add rather than
   * compete: the inset outside, the gutter inside. Do not merge them back.
   *
   * `min-h-dvh` stays on the root — `safe-area.test.tsx` finds this screen by it.
   */
  return (
    <div className="px-safe pt-safe pb-safe flex min-h-dvh flex-col justify-center">
      <div className="mx-auto w-full max-w-xl px-4 py-10">
        <StorageWarning />

        <header className="mb-8">
          <h1 className="text-4xl font-black tracking-tight text-ink sm:text-5xl">Managing Up</h1>
          <p className="mt-3 text-base text-ink-muted">
            Immerse yourself in a strategic satire about weaponizing office speak, rallying allies
            to your corner, and manipulating company channels to survive the workday. Craft the
            ultimate corporate spin, build fragile alliances, and ultimately convince senior
            management to adopt your agenda.
          </p>
        </header>

        <Card>
          <CardBody className="flex flex-col gap-3">
            {run ? (
              <Button
                variant="primary"
                size="lg"
                data-testid="continue"
                onClick={() => {
                  void navigate(`/play/${run.missionId}`);
                }}
              >
                <span className="flex flex-col items-start">
                  <span>Continue — {run.missionTitle}</span>
                  <span className="text-xs font-normal opacity-90">
                    Day {run.day}, {run.phase} · {run.profileLabel}
                  </span>
                </span>
              </Button>
            ) : (
              <AsyncBoundary result={catalog} label="the mission catalog">
                {(result) => {
                  const start = firstPlayable(
                    result.catalog,
                    profile ? completedMissionIds(profile) : [],
                  );
                  return start ? (
                    <ButtonLink
                      variant="primary"
                      size="lg"
                      to={`/play/${start}`}
                      data-testid="start"
                    >
                      Start your first day
                    </ButtonLink>
                  ) : (
                    <p className="text-sm text-ink-muted" data-testid="no-missions">
                      No missions are on this device yet. Connect once and the whole campaign is
                      saved here, ready to play offline.
                    </p>
                  );
                }}
              </AsyncBoundary>
            )}

            <ButtonLink to="/missions" size="lg" data-testid="title-missions">
              Missions
            </ButtonLink>
            <ButtonLink to="/career" size="lg" data-testid="title-career">
              Career
            </ButtonLink>
            <ButtonLink to="/how-to-play" size="lg" data-testid="title-how-to-play">
              How to Play
            </ButtonLink>
            <ButtonLink to="/settings" size="lg" data-testid="title-settings">
              Settings
            </ButtonLink>
          </CardBody>
        </Card>

        {profile ? (
          <p className="mt-6 text-center text-xs text-ink-subtle">
            Playing as <span className="font-medium text-ink-muted">{profile.name}</span> ·{' '}
            {profile.career.cxp.toLocaleString()} CXP banked
          </p>
        ) : null}
      </div>
    </div>
  );
}
